import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setLoading } from '../../../redux/store/common/slice'
import { getUserContractCategories } from 'features/UserContractCategories/api'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import {
  UserContractCategoriesParams,
  UserContractCategoriesRequestData,
  UserContractCategory
} from 'features/UserContractCategories/types'
import { TPageInfo } from '../../../components/Table/types'
import { getUser } from '../../../redux/store/user/getters'
import { useDebounce } from '../../../hooks/hooks'

type ContextProps = {
  state: {
    totalNumber: number
    searchValue: string
    debouncedSearch: string
    sortParams?: UserContractCategoriesRequestData
    pageInfo: TPageInfo
    tableData: UserContractCategory[]
  }
  actions: {
    getContractCategories: (
      args: UserContractCategoriesRequestData
    ) => Promise<void>
    setPageInfo: Dispatch<SetStateAction<TPageInfo>>
    setSearchValue: Dispatch<SetStateAction<string>>
    handleChangePageSize: (pageSize: number) => void
    handleTableChange: (pagination, _filters, sorter) => void
  }
}

const UserProfileContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const UserProfileContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [totalNumber, setTotalNumber] = useState(0)
  const [sortParams, setSortParams] =
    useState<UserContractCategoriesRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<UserContractCategory[]>([])
  const user = useSelector(getUser)
  const debouncedSearch = useDebounce(searchValue, 500)
  const dispatch = useDispatch()

  useEffect(() => {
    if (user.uuid) {
      setPageInfo({ ...DEFAULT_PAGE })
      getContractCategories({
        ...sortParams,
        page: { ...DEFAULT_PAGE },
        search: debouncedSearch
      })
    }
    // eslint-disable-next-line
    }, [user.uuid, debouncedSearch])

  const getContractCategories = useCallback(
    async ({
      sortField,
      sortOrder,
      page,
      search
    }: UserContractCategoriesRequestData) => {
      try {
        dispatch(setLoading(true))
        const params: UserContractCategoriesParams = {}
        if (sortField) {
          if (sortOrder) {
            params.ordering = `${
              sortOrder === 'descend' ? '-' : ''
            }${sortField}`
          }
          setSortParams({
            sortField,
            sortOrder
          })
        }
        if (search) {
          params.search = search
        }
        const dataPage = page ?? pageInfo
        params.limit = dataPage.pageSize
        params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
        const { data } = await getUserContractCategories(user.uuid, params)
        setTableData(
          data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        if (!search?.length) {
          setTotalNumber(data.count)
        }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, pageInfo, user.uuid]
  )

  const handleChangePageSize = useCallback(
    async (pageSize) => {
      const page = {
        pageNumber: 1,
        pageSize
      }
      await getContractCategories({
        ...sortParams,
        page,
        search: debouncedSearch
      })
      setPageInfo(page)
    },
    [debouncedSearch, getContractCategories, sortParams]
  )

  const handleTableChange = useCallback(
    async (pagination, _filters, sorter) => {
      const page = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize
      }
      await getContractCategories({
        sortField: sorter.field,
        sortOrder: sorter.order,
        page,
        search: debouncedSearch
      })
      setPageInfo(page)
    },
    [debouncedSearch, getContractCategories]
  )

  const context = useMemo(
    () => ({
      state: {
        totalNumber,
        searchValue,
        debouncedSearch,
        sortParams,
        pageInfo,
        tableData
      },
      actions: {
        getContractCategories,
        setPageInfo,
        setSearchValue,
        handleChangePageSize,
        handleTableChange
      }
    }),
    [
      totalNumber,
      searchValue,
      debouncedSearch,
      sortParams,
      pageInfo,
      tableData,
      getContractCategories,
      handleChangePageSize,
      handleTableChange
    ]
  )

  return (
    <UserProfileContext.Provider value={context}>
      {children}
    </UserProfileContext.Provider>
  )
}

export const useUserProfileContext = () => useContext(UserProfileContext)

export default UserProfileContextProvider
